/**
 * Single source of truth for "is this course a paid trial, and what does it cost?"
 *
 * Trial flags travel through several paths (learning-journey query, course player,
 * admin course dialog, landing page) and environments (test vs live). `access_mode`
 * has historically been missing or stale in one environment while `trial_price`
 * was set in the other, which made the journey card fall back to the generic
 * "Unlock" label instead of "Try for ₹1". So never branch on a single field:
 * resolve the offer through these helpers everywhere.
 */

import { INTRO_OFFERS_ENABLED } from "@/lib/introPassPath";

export const DEFAULT_TRIAL_PRICE = 99;

export interface TrialPricingSource {
  access_mode?: string | null;
  trial_price?: number | string | null;
  trial_currency?: string | null;
  is_landing_trial?: boolean | null;
  is_paid?: boolean | null;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: "₹",
  USD: "$",
  EUR: "€",
  GBP: "£",
  AED: "AED ",
};

/**
 * Parses a trial price coming from Postgres numeric (often a string), a JSON
 * round-trip, or a hand-edited admin field. Returns null when the value cannot
 * be read as a usable positive amount.
 */
export function parseTrialPrice(raw: unknown): number | null {
  if (raw === null || raw === undefined || typeof raw === "boolean") return null;
  let value: number;
  if (typeof raw === "number") {
    value = raw;
  } else if (typeof raw === "string") {
    const cleaned = raw.replace(/[^0-9.\-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
    value = Number(cleaned);
  } else {
    return null;
  }
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

/** True when the course should be offered as a paid trial, regardless of which flag survived. */
export function isTrialCourse(course: TrialPricingSource | null | undefined): boolean {
  if (!course || !INTRO_OFFERS_ENABLED) return false;
  // An explicitly free course is never a trial, even with leftover trial fields.
  if (course.is_paid === false) return false;
  const mode = typeof course.access_mode === "string" ? course.access_mode.trim().toLowerCase() : "";
  if (mode === "trial") return true;
  if (course.is_landing_trial === true) return true;
  return parseTrialPrice(course.trial_price) !== null;
}

/** The amount to charge/display, falling back to the platform default trial price. */
export function resolveTrialPrice(course: TrialPricingSource | null | undefined): number {
  return parseTrialPrice(course?.trial_price) ?? DEFAULT_TRIAL_PRICE;
}

export function formatTrialPrice(amount: number, currency?: string | null): string {
  const code = (currency || "INR").trim().toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
  const rounded = Math.round(amount);
  const formatted = code === "INR" ? rounded.toLocaleString("en-IN") : rounded.toLocaleString("en-US");
  return `${symbol}${formatted}`;
}

export interface TrialOffer {
  isTrial: boolean;
  price: number;
  label: string;
  /** e.g. "Try for ₹1" */
  ctaLabel: string;
}

export function resolveTrialOffer(course: TrialPricingSource | null | undefined): TrialOffer {
  const price = resolveTrialPrice(course);
  const label = formatTrialPrice(price, course?.trial_currency);
  return {
    isTrial: isTrialCourse(course),
    price,
    label,
    ctaLabel: `Try for ${label}`,
  };
}
